import { FiPlus } from 'react-icons/fi';
import { MdQrCodeScanner } from 'react-icons/md';
import FilterTable, { BaseData } from './Table/Table';
import { ScannerTypeDTO } from '../models/scanner-type';
import Button from './atoms/Button';
import EmptyTable from './Table/EmptyTable';
import MenuDropdown from './atoms/MenuDropdown';

interface Props {
  scannerTypes: ScannerTypeDTO[];
  isLoading: boolean;
  onAdd: () => void;
  onEdit: (scannerType: ScannerTypeDTO) => void;
  onDelete: (scannerType: ScannerTypeDTO) => void;
}

interface TableScannerTypeItem extends BaseData {
  id: string;
  name: string;
  description: string | JSX.Element;
  status: JSX.Element;
}

const ScannerTypesTable: React.FC<Props> = ({
  scannerTypes, isLoading, onAdd, onEdit, onDelete,
}) => {
  const headCells = [
    { id: 'name' as const, label: 'Nombre' },
    { id: 'description' as const, label: 'Descripción' },
    { id: 'status' as const, label: 'Estado' },
  ];

  const tableRows: TableScannerTypeItem[] = scannerTypes.map((st) => ({
    id: st.id,
    name: st.name,
    description: st.description || <span className="text-text-tertiary">-</span>,
    status: st.is_active
      ? <div className="tag-primary">Activo</div>
      : <div className="tag-warning">Inactivo</div>,
  }));

  const actions = (row: TableScannerTypeItem) => {
    const scannerType = scannerTypes.find((st) => st.id === row.id);
    if (!scannerType) {
      return <></>;
    }
    return (
      <MenuDropdown
        data={row}
        options={[
          {
            label: 'Editar',
            onClick: () => onEdit(scannerType),
          },
          {
            label: 'Eliminar',
            onClick: () => onDelete(scannerType),
          },
        ]}
      />
    );
  };

  const customButton = [
    <Button onClick={onAdd} variant="secondary">
      <div className="flex flex-row gap-2 items-center">
        <FiPlus className="text-xl" />
        <p className="whitespace-nowrap hidden md:block">Crear tipo de escáner</p>
      </div>
    </Button>,
  ];

  if (isLoading) {
    return <div className="animate-pulse h-32 bg-surface-secondary rounded-3xl" />;
  }

  return tableRows.length > 0 ? (
    <FilterTable
      rows={tableRows}
      headCells={headCells}
      actions={actions}
      customButtons={customButton}
      pagination={{ pageSize: 10, enabled: true }}
      searchEnabled={true}
      tableId="scanner-types-table"
    />
  ) : (
    <EmptyTable
      title="No hay tipos de escáner"
      info="Crea un tipo de escáner para restringir qué entradas puede escanear cada escáner"
      icon={MdQrCodeScanner}
      iconColor="text-secondary-500"
      button={[{
        label: 'Crear tipo de escáner',
        onClick: onAdd,
        variant: 'secondary',
        icon: FiPlus,
      }]}
    />
  );
};

export default ScannerTypesTable;
